import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import FeeRuleRow, { effectiveFee } from './FeeRuleRow';
import { toast } from "react-toastify";
import { Button, Select, Spinner, Table } from '@opendate-app/setlist';

const axios = require("axios").default;

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const priceRangeLabel = (feeRule, index, count) => {
  if (index === 0) {
    return currencyFormatter.format(feeRule.maximum);
  }

  if (index === count - 1) {
    return `${currencyFormatter.format(feeRule.minimum)} +`;
  }

  return `${currencyFormatter.format(feeRule.minimum)} - ${currencyFormatter.format(feeRule.maximum)}`;
};

const mutedTextStyle = { color: "var(--sl-text-tertiary)" };

// Where the per-event overrides live. A confirm saves them as they are made; a
// series wizard passes its own store that edits the unsaved plan.
export const confirmFeeRuleOverrideStore = (team, confirm) => {
  const overridesPath = "/teams/" + team.id + "/calendar_events/" + confirm.id + "/calendar_event_fee_rules";
  return {
    listOverrides: () => axios.get(overridesPath).then(data => data.data),
    saveOverride: (existing, feeRuleId, changes) => (existing ? (
      axios.put(overridesPath + "/" + existing.id, { calendar_event_fee_rule: changes })
    ) : (
      axios.post(overridesPath, {
        calendar_event_fee_rule: {
          calendar_event_id: confirm.id,
          fee_rule_id: feeRuleId,
          ...changes
        }
      })
    )),
    destroyOverride: (calendarEventFeeRuleId) => axios.delete(overridesPath + "/" + calendarEventFeeRuleId),
  };
};

const FeeRuleTable = ({
  csrfToken,
  confirm,
  team,
  feeRuleSets,
  eventFeeComponentCount,
  store,
  forSeries = false
}) => {
  const defaultStore = useMemo(() => confirmFeeRuleOverrideStore(team, confirm), [team.id, confirm.id]);
  const overrides = store || defaultStore;
  const feeRulesLocked = eventFeeComponentCount === null || eventFeeComponentCount > 0;

  const [canSubmit, setCanSubmit] = useState(false);
  const [feeRules, setFeeRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  // Keyed by fee rule id: only the fields the user changed from what this event charges now.
  const [pendingChanges, setPendingChanges] = useState({});

  const [onlineFeeRuleSet, setOnlineFeeRuleSet] = useState(
    () => feeRuleSets.find(feeRuleSet => feeRuleSet.use_case === "ONLINE")
  );

  const [atDoorCardFeeRuleSet, setAtDoorCardFeeRuleSet] = useState(
    () => feeRuleSets.find(feeRuleSet => feeRuleSet.use_case === "AT_DOOR_CARD")
  );

  const [atDoorCashFeeRuleSet, setAtDoorCashFeeRuleSet] = useState(
    () => feeRuleSets.find(feeRuleSet => feeRuleSet.use_case === "AT_DOOR_CASH")
  );

  const [selectedFeeRuleSet, setSelectedFeeRuleSet] = useState(onlineFeeRuleSet);

  const showToastError = (err) => {
    const body = err?.response?.data;
    toast.error(Array.isArray(body) ? body.join(", ") : "Something went wrong. Try again.", {
      position: toast.POSITION.TOP_CENTER,
      draggable: false,
      closeOnClick: false,
      autoClose: 5000,
      hideProgressBar: true
    });
  }

  const getCalendarEventFeeRules = (feeRules) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    overrides.listOverrides()
      .then(calendarEventFeeRules => {
        setFeeRules(feeRules.map(feeRule => {
          feeRule.calendar_event_fee_rule = calendarEventFeeRules.find(calendarEventFeeRule => {
            return calendarEventFeeRule.fee_rule_id === feeRule.id
          });
          return feeRule;
        }));
        setIsLoading(false);
        setIsProcessing(false);
      })
      .catch(err => {
        showToastError(err);
        setIsLoading(false);
        setIsProcessing(false);
      });
  };

  const getFeeRules = () => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.get("/teams/" + team.id + "/fee_rules", {
        params: {
          "q[fee_rule_set_id_eq]": selectedFeeRuleSet.id
        }
      })
      .then(data => {
        getCalendarEventFeeRules(data.data);
      })
      .catch(err => {
        showToastError(err);
        setIsLoading(false);
        setIsProcessing(false);
      });
  };

  const reInitializeState = () => {
    setCanSubmit(false);
    setPendingChanges({});
    getFeeRules();
  };

  useEffect(() => {
    reInitializeState();
  }, [selectedFeeRuleSet]);

  useEffect(() => {
    setCanSubmit(Object.keys(pendingChanges).length > 0);
  }, [pendingChanges]);

  const onRowChange = (feeRule, field, value) => {
    setPendingChanges(current => {
      const {[feeRule.id]: rowChanges = {}, ...otherRows} = current;
      const {[field]: _previous, ...otherFields} = rowChanges;
      // A cleared input is no change; Reset is the only way back to the team value.
      const unchanged = Number.isNaN(value) || value === effectiveFee(feeRule, field);
      const nextRow = unchanged ? otherFields : {...otherFields, [field]: value};

      return Object.keys(nextRow).length > 0 ? {...otherRows, [feeRule.id]: nextRow} : otherRows;
    });
  }

  const handleSubmit = () => {
    setIsProcessing(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    var requestPromises = [];

    for (const feeRuleID in pendingChanges) {
      const changes = pendingChanges[feeRuleID];
      const existing = feeRules.find(feeRule => String(feeRule.id) === feeRuleID).calendar_event_fee_rule;
      const request = overrides.saveOverride(existing, feeRuleID, changes);

      requestPromises.push(
        request.catch(err => {
          showToastError(err);
        })
      );
    }

    axios.all(requestPromises).then(() => {
      reInitializeState();
    });
  };

  // A series has no Save here: its own header saves the whole plan, so a row
  // goes into the store as soon as the user leaves it, or that save would miss
  // it. A value equal to the team's own is no override, so the dates keep
  // following the team's fee table. The store is local, so only this row is
  // refreshed, with nothing disabled and no reload: the user is already in the
  // next field.
  const commitRow = (feeRule) => {
    const changes = pendingChanges[feeRule.id];
    setPendingChanges(({ [feeRule.id]: _committed, ...rest }) => rest);

    overrides.listOverrides()
      .then((stored) => {
        if (!changes) return null;

        const existing = stored.find((override) => override.fee_rule_id === feeRule.id);
        const next = Object.fromEntries(Object.entries(changes).map(([field, value]) => (
          [field, value === parseFloat(feeRule[field]) ? null : value]
        )));
        const kept = (field) => (field in next ? next[field] : existing?.[field] ?? null);
        if (kept("organizer_service_fee") == null && kept("organizer_service_fee_percentage") == null) {
          return existing ? overrides.destroyOverride(existing.id) : null;
        }
        return overrides.saveOverride(existing, feeRule.id, next);
      })
      .catch(showToastError)
      .then(() => refreshRowFromStore(feeRule.id));
  };

  const refreshRowFromStore = (feeRuleId) => overrides.listOverrides().then((stored) => {
    setFeeRules((rules) => rules.map((rule) => (rule.id === feeRuleId ? {
      ...rule,
      calendar_event_fee_rule: stored.find((override) => override.fee_rule_id === rule.id)
    } : rule)));
  });

  const handleReset = (calendarEventFeeRuleId) => {
    if (forSeries) {
      const feeRule = feeRules.find((rule) => rule.calendar_event_fee_rule?.id === calendarEventFeeRuleId);
      setPendingChanges(({ [feeRule.id]: _reset, ...rest }) => rest);
      overrides.destroyOverride(calendarEventFeeRuleId)
        .catch(showToastError)
        .then(() => refreshRowFromStore(feeRule.id));
      return;
    }

    setIsProcessing(true);

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    overrides.destroyOverride(calendarEventFeeRuleId)
      .then(() => {
        reInitializeState();
      })
      .catch(err => {
        showToastError(err);
        reInitializeState();
      });
  };

  const feeRuleSetOptions = [
    { value: onlineFeeRuleSet.id, label: "Online" },
    { value: atDoorCardFeeRuleSet.id, label: "At the door - Card" },
    { value: atDoorCashFeeRuleSet.id, label: "At the door - Cash" },
  ];

  const columns = [
    {
      key: "price",
      header: "Ticket Price",
      render: (feeRule) => (
        <strong>{priceRangeLabel(feeRule, feeRules.indexOf(feeRule), feeRules.length)}</strong>
      ),
    },
    {
      key: "fee",
      header: "Your Fee",
      render: (feeRule) => (
        <FeeRuleRow
          feeRule={feeRule}
          handleChange={(field, value) => {
            onRowChange(feeRule, field, value)
          }}
          handleReset={handleReset}
          handleBlur={forSeries ? () => commitRow(feeRule) : undefined}
          isDisabled={
            isProcessing ||
            feeRule.maximum === "0.0" ||
            feeRulesLocked
          }
        />
      ),
    },
  ];

  return (
    <div style={{"marginTop": "40px", "color": "var(--sl-text-primary)"}}>
      <div className="row">
        <div className="col-12 col-md">
          <p className="mb-2">
            <strong>Organizer Fees</strong>
          </p>
          <p className="small mb-1" style={mutedTextStyle}>
            You will earn the following fees on each ticket sale, based on individual ticket price: a flat amount plus a percentage of the ticket price.
          </p>
          {eventFeeComponentCount > 0 ? (
            <p className="small mb-1" style={mutedTextStyle}>
              This show charges the fees listed under General Ledger above, so the fees below
              do not apply to it. Remove them to charge these fees instead.
            </p>
          ) : null}
          {eventFeeComponentCount === 0 ? (
            <p className="small mb-1" style={mutedTextStyle}>
              {forSeries
                ? "You may customize the flat amount and percentage for every date in this series in the Your Fee column below."
                : "You may customize the flat amount and percentage for this show in the Your Fee column below."}
            </p>
          ) : null}
        </div>
        <div className="col-12 mt-2">
          <Select
            size="sm"
            id="selected-fee-rule-set"
            aria-label="Fee rule set"
            width={200}
            options={feeRuleSetOptions}
            value={selectedFeeRuleSet.id}
            onChange={
              (feeRuleSetId) => {
                setSelectedFeeRuleSet(
                  feeRuleSets.find(feeRuleSet => feeRuleSet.id === feeRuleSetId)
                );
              }
            }
          />
        </div>
      </div>
      {
        isLoading ? (
          <div className="d-flex justify-content-center">
            <Spinner aria-label="Loading organizer fees" />
          </div>
        ) : (
          <div className="row">
            <div className="col-12 col-md" style={{"marginTop": "15px", "marginBottom": "25px", "maxWidth": "600px"}}>
              <Table
                columns={columns}
                data={feeRules}
                getRowKey={(feeRule) => feeRule.id}
              />
            </div>
          </div>
        )
      }
      {forSeries ? null : (
        <div className="row">
          <div className="col-12 col-md">
            <Button
              variant="primary"
              size="sm"
              disabled={!canSubmit || feeRulesLocked}
              onClick={handleSubmit}
            >
              Save Changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

FeeRuleTable.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  team: PropTypes.object.isRequired,
  feeRuleSets: PropTypes.array,
  eventFeeComponentCount: PropTypes.number,
  store: PropTypes.shape({
    listOverrides: PropTypes.func.isRequired,
    saveOverride: PropTypes.func.isRequired,
    destroyOverride: PropTypes.func.isRequired,
  }),
  forSeries: PropTypes.bool
};

export default FeeRuleTable;
